import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { RedirectError } from '#src/shared/error';

import { getViewerSignature } from '#src/app/shared/api/signature';

import {
	PetitionActionsPageInfo,
	type PetitionActionsPageInfoQuery,
	type PetitionActionsPageInfoQueryVariables,
} from './api.graphql';
import { getPetitionActionsContactDmConfig } from './getPetitionActionsContactDmConfig';

export type PsfContactDecisionMakerExperiment = PetitionActionsPageInfoQuery['experiments']['psfContactDecisionMaker'];
export type PsfContactDecisionMakerExperimentVariation = PsfContactDecisionMakerExperiment['variation'];
export type SupporterVoicesVideoFirstExperiment =
	PetitionActionsPageInfoQuery['experiments']['supporterVoicesVideoFirst'];
export type SupporterVoicesVideoFirstExperimentVariation = SupporterVoicesVideoFirstExperiment['variation'];
export type SupporterVoicesCommentToScriptExperiment =
	PetitionActionsPageInfoQuery['experiments']['supporterVoicesCommentToScript'];
export type SupporterVoicesCommentToScriptExperimentVariation = SupporterVoicesCommentToScriptExperiment['variation'];
export type PsfContactDecisionMaker = NonNullable<
	PetitionActionsPageInfoQuery['petition']
>['decisionMakers']['nodes'][number];

type PetitionActionsInfoResult = {
	petition: NonNullable<PetitionActionsPageInfoQuery['petition']>;
	hasSigned: boolean;
	featureConfigs: PetitionActionsPageInfoQuery['featureConfigs'];
	psfContactDecisionMakerExperiment: PsfContactDecisionMakerExperiment;
	supporterVoicesVideoFirstExperiment: SupporterVoicesVideoFirstExperiment;
	supporterVoicesCommentToScriptExperiment: SupporterVoicesCommentToScriptExperiment;
};

export async function getPetitionActionsPageData(
	utilityContext: UtilityContext,
	slug: string,
	userId: string | undefined,
): Promise<PetitionActionsInfoResult> {
	const { gql } = utilityContext;

	const { emailEnabled } = await getPetitionActionsContactDmConfig(utilityContext);

	const [{ data }, signature] = await Promise.all([
		gql.fetch<PetitionActionsPageInfoQuery, PetitionActionsPageInfoQueryVariables>({
			query: PetitionActionsPageInfo,
			variables: { slugOrId: slug, emailEnabled },
			rejectOnError: true,
		}),
		userId ? getViewerSignature(slug, utilityContext) : Promise.resolve(undefined),
	]);

	if (!data.petition) {
		throw new RedirectError({ url: `/p/${encodeURIComponent(slug)}`, reason: 'petition-not-found' });
	}

	return {
		petition: data.petition,
		hasSigned: !!signature,
		featureConfigs: data.featureConfigs,
		psfContactDecisionMakerExperiment: data.experiments.psfContactDecisionMaker,
		supporterVoicesVideoFirstExperiment: data.experiments.supporterVoicesVideoFirst,
		supporterVoicesCommentToScriptExperiment: data.experiments.supporterVoicesCommentToScript,
	};
}
